// Store current user for role checking
let currentUser = null;

// Store debug verbosity level (1 = minimal, 2 = detailed, 3 = verbose)
let debugVerbosity = 1;

// Set the current user (called from App.js after auth)
export function setDebugUser(user) {
  currentUser = user;
}

// Get current debug verbosity level
export function getDebugVerbosity() {
  const stored = localStorage.getItem('ghost_debug_verbosity');
  if (stored) {
    debugVerbosity = parseInt(stored, 10);
  }
  return debugVerbosity;
}

// Set debug verbosity level
export function setDebugVerbosity(level) {
  if (!hasDebugPermission()) {
    console.log('❌ Debug mode requires admin or super_admin role');
    return false;
  }

  const validLevel = Math.max(1, Math.min(3, parseInt(level, 10) || 1));
  debugVerbosity = validLevel;
  localStorage.setItem('ghost_debug_verbosity', validLevel.toString());
  console.log(`🐛 Debug verbosity set to level ${validLevel}`);
  console.log(`  Level 1: User actions (open, save, create, delete, move, rename, load, get)`);
  console.log(`  Level 2: + Detailed parameters`);
  console.log(`  Level 3: + Internal operations (Component.method, update* functions)`);
  return true;
}

// Check if user has permission to use debug mode
function hasDebugPermission() {
  if (!currentUser) return false;
  return currentUser.role === 'admin' || currentUser.role === 'super_admin';
}

// Check if debug mode is enabled via:
// 1. User must be admin or super_admin (always required)
// 2. localStorage flag (for enabling/disabling)
export function isDebugEnabled() {
  // Always check user permission first
  if (!hasDebugPermission()) {
    return false;
  }

  // Check if user explicitly disabled debug mode
  const localStorageDebug = localStorage.getItem('ghost_debug_mode');
  if (localStorageDebug === 'false') {
    return false;
  }

  // User has permission - check if enabled via localStorage (default true for admins)
  return localStorageDebug === 'true' || localStorageDebug === null;
}

// Enable/disable debug mode in production
export function setDebugMode(enabled) {
  if (!hasDebugPermission()) {
    console.log('❌ Debug mode requires admin or super_admin role');
    return false;
  }

  if (enabled) {
    localStorage.setItem('ghost_debug_mode', 'true');
    getDebugVerbosity(); // Initialize verbosity from storage
    console.log('🐛 Debug mode ENABLED. Console logs will now appear.');
    console.log(`   Current verbosity: Level ${debugVerbosity}`);
    console.log(`   Change with: ghostDebug.setLevel(1|2|3)`);
  } else {
    localStorage.setItem('ghost_debug_mode', 'false');
    console.log('✓ Debug mode DISABLED. Console is now clean.');
  }
  return true;
}

// Debug group nesting depth
let auditDepth = 0;

function normalizeDebugInvocation(functionName, args) {
  if (typeof functionName !== 'string') {
    return { functionName, args };
  }

  const match = functionName.match(/^\[([^\]]+)\]\s*(.*)$/);
  if (!match) {
    return { functionName, args };
  }

  const [, label, inlineMessage] = match;
  if (!inlineMessage) {
    return { functionName: label, args };
  }

  return { functionName: label, args: [inlineMessage, ...args] };
}

// Consolidated debug function
export function debug(functionName, options = {}, ...args) {
  if (!isDebugEnabled()) return;

  // Handle legacy calls where options might not be an object
  if (typeof options !== 'object' || options === null) {
    args = [options, ...args];
    options = {};
  }

  const normalized = normalizeDebugInvocation(functionName, args);
  functionName = normalized.functionName;
  args = normalized.args;

  // Set defaults
  options.type = options.type || 'log';
  if (options.type === 'audit') {
    options.level = options.level || 3;
  }
  options.group = options.group || false;

  const indent = '  '.repeat(auditDepth);

  if (options.group === 'start') {
    // Start a group
    console.groupCollapsed(`${indent}[${functionName}]`);
    if (args.length > 0 && getDebugVerbosity() >= 2) {
      args.forEach((arg, i) => {
        const summary =
          Array.isArray(arg) ? `Array(${arg.length})` :
          arg instanceof Map ? `Map(${arg.size})` :
          arg instanceof Set ? `Set(${arg.size})` :
          typeof arg === 'function' ? 'function' :
          arg === null ? 'null' :
          arg === undefined ? 'undefined' :
          typeof arg === 'object' ? `Object(${Object.keys(arg).length} keys)` :
          typeof arg;

        console.log(`arg[${i}] (${summary}):`, arg);
      });
    }
    auditDepth++;
  } else if (options.group === 'end') {
    // End a group
    auditDepth = Math.max(0, auditDepth - 1);
    console.groupEnd();
  } else {
    // Regular logging
    if (options.type === 'log') {
      console.log(`[${functionName}]`, ...args);
    } else if (options.type === 'error') {
      console.error(`[${functionName}]`, ...args);
    } else if (options.type === 'warn') {
      console.warn(`[${functionName}]`, ...args);
    } else if (options.type === 'audit') {
      const currentLevel = getDebugVerbosity();
      if (options.level > currentLevel) return;

      if (currentLevel === 1) {
        console.log(`[${functionName}]`);
      } else {
        console.groupCollapsed(`${indent}[${functionName}]`);
        args.forEach((arg, i) => {
          const summary =
            Array.isArray(arg) ? `Array(${arg.length})` :
            arg instanceof Map ? `Map(${arg.size})` :
            arg instanceof Set ? `Set(${arg.size})` :
            typeof arg === 'function' ? 'function' :
            arg === null ? 'null' :
            arg === undefined ? 'undefined' :
            typeof arg === 'object' ? `Object(${Object.keys(arg).length} keys)` :
            typeof arg;

          console.log(`arg[${i}] (${summary}):`, arg);
        });
        console.groupEnd();
      }
    }
  }
}

// Legacy aliases for backwards compatibility
export function debugError(functionName, ...args) {
  return debug(functionName, { type: 'error' }, ...args);
}

export function debugWarn(functionName, ...args) {
  return debug(functionName, { type: 'warn' }, ...args);
}

export function debugAudit(functionName, level = 3, ...args) {
  return debug(functionName, { type: 'audit', level }, ...args);
}

export function debugAuditGroup(functionName, level = 3, ...args) {
  return debug(functionName, { type: 'audit', level, group: 'start' }, ...args);
}

export function debugAuditGroupEnd() {
  return debug(null, { group: 'end' });
}

// Legacy aliases
export const auditTreeInputs = debugAudit;
export const debugLog = debug;

// Expose debug controls to window for easy access in production
if (typeof window !== 'undefined') {
  window.ghostDebug = {
    enable: () => setDebugMode(true),
    disable: () => setDebugMode(false),
    setLevel: (level) => setDebugVerbosity(level),
    getLevel: () => getDebugVerbosity(),
    getUser: () => {
      console.log('Current user:', currentUser);
      console.log('Has permission:', hasDebugPermission());
      return currentUser;
    },
    test: () => {
      console.log('Testing debug function...');
      console.log('isDebugEnabled():', isDebugEnabled());
      console.log('hasDebugPermission():', hasDebugPermission());
      console.log('currentUser:', currentUser);
      debug('TEST.ghostDebug.test', { type: 'log' }, 'This is a test message');
      debug('TEST.audit', { type: 'audit', level: 1 }, 'Audit level 1');
      debug('TEST.audit', { type: 'audit', level: 2 }, 'Audit level 2');
      debug('TEST.audit', { type: 'audit', level: 3 }, 'Audit level 3');
      return 'Test complete - check console above';
    },
    status: () => {
      const enabled = isDebugEnabled();
      const level = getDebugVerbosity();
      console.log(`Debug mode: ${enabled ? 'ON ✅' : 'OFF ❌'}`);
      console.log(`User: ${currentUser ? currentUser.email : 'None'} (${currentUser?.role || 'no role'})`);
      console.log(`Has permission: ${hasDebugPermission() ? 'YES ✅' : 'NO ❌'}`);
      if (enabled) {
        console.log(`Verbosity: Level ${level}`);
        console.log(`  Level 1: User actions (open, save, create, delete, move, rename, load, get)`);
        console.log(`  Level 2: + Detailed parameters`);
        console.log(`  Level 3: + Internal operations (Component.method, update* functions)`);
      }
      return { enabled, level, user: currentUser, hasPermission: hasDebugPermission() };
    },
    storage: () => {
      const items = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        try {
          items[key] = JSON.parse(localStorage.getItem(key));
        } catch {
          items[key] = localStorage.getItem(key);
        }
      }
      console.table(items);
      return items;
    },
  };

  // Only show instructions if debug mode is enabled
  if (!window.__ghostDebugInit && isDebugEnabled()) {
    console.log('%cGhost Debug Mode', 'font-size: 16px; font-weight: bold; color: #374753;');
    console.log('%cUse ghostDebug.enable() / ghostDebug.disable() / ghostDebug.setLevel(1-3) / ghostDebug.status()', 'color: #2B3740;');
    console.log(`%cCurrent level: ${getDebugVerbosity()}`, 'color: #6B7280;');
    window.__ghostDebugInit = true;
  }
}